import { CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "./../../core/Constants";
import { findQuestionInService } from "./../shared";
import { areAllFieldsCompleted } from "./../question-utils";
import { ceil } from "./../utils";
import { getServiceStats } from "./service-stats";
import {
  getRecurringServicePanelTableRows,
  getPayrollServicePanelTableRows,
} from "./panel-rows";

function getIsServiceCompleted(
  service: DTOs.ServiceDTO,
  businessUnit: Constants.BusinessUnit
): boolean {
  const visibileQuestions = service.data.questions.filter(
    (item) =>
      item.state.isShown &&
      item.data.businessUnits.includes(businessUnit as Constants.BusinessUnit)
  );

  const questionsWithAnswerField = visibileQuestions.filter(
    (item) => !item.state.isInfoTextVariat && item.data.inputType
  );
  const isAllFilled = questionsWithAnswerField.every((item) => {
    return item.state.isFilled && areAllFieldsCompleted(item);
  });

  return isAllFilled;
}

export function getAreAllServicesCompleted(
  services: Array<DTOs.ServiceDTO>,
  businessUnit: Constants.BusinessUnit
): boolean {
  return services.every((service: DTOs.ServiceDTO) => {
    return getIsServiceCompleted(service, businessUnit);
  });
}

export function isDigitallySigningChecked(
  appState: CoreInterfaces.AppState
): boolean {
  const q0017Value = findQuestionInService(
    appState.services.find(
      (service) =>
        service.data.code === Constants.ServiceCode.GeneralInformation
    ),
    Constants.GeneralInformationQuestion.Q0017
  )?.data?.userValue;

  return q0017Value === Constants.YesNo.Yes;
}

export function isOutdatedVersion(
  globalState: CoreInterfaces.AppState
): boolean {
  return (
    globalState.currentConfiguration.status ===
    Constants.ConfigurationStatus.OutdatedVersion
  );
}

export function isFinalorOutdatedVersion(
  globalState: CoreInterfaces.AppState
): boolean {
  return (
    globalState.currentConfiguration.status ===
      Constants.ConfigurationStatus.FinalVersion ||
    globalState.currentConfiguration.status ===
      Constants.ConfigurationStatus.OutdatedVersion
  );
}

export function getEngagementMonthlyStats(
  appState: CoreInterfaces.AppState
): CoreInterfaces.EngagementMonthlyPartialStats {
  const serviceTotalsPack: CoreInterfaces.EngagementMonthlyPartialStats = {
    totalPricePerMonth: 0,
    totalWorkTime: 0,
  };

  const monthlyRecurringServicesTotalsRow: CoreInterfaces.RecurringServicesTableRow =
    getRecurringServicePanelTableRows(
      appState,
      Constants.ServiceRecurringType.Monthly
    ).find(
      (row) =>
        row.id === Constants.CustomDataGridColumnId.RecurringServicesTotal
    );
  const payrollRecurringServicesTotalsRow: CoreInterfaces.PayrollServicesTableRow =
    getPayrollServicePanelTableRows(appState).find(
      (row) => row.id === Constants.CustomDataGridColumnId.PayrollServicesTotal
    );

  if (!!monthlyRecurringServicesTotalsRow) {
    serviceTotalsPack.totalWorkTime +=
      monthlyRecurringServicesTotalsRow.minutesPerPeriodUnderlineValue;
    serviceTotalsPack.totalPricePerMonth +=
      monthlyRecurringServicesTotalsRow.totalPricePerPeriodUnderlineValue;
  }
  if (!!payrollRecurringServicesTotalsRow) {
    serviceTotalsPack.totalWorkTime +=
      payrollRecurringServicesTotalsRow.minutesPerPeriodUnderlineValue;
    serviceTotalsPack.totalPricePerMonth +=
      payrollRecurringServicesTotalsRow.totalPricePerPeriodUnderlineValue;
  }

  return serviceTotalsPack;
}

// this sums the total price/year for the first 5 tables
export function getEngagementTotals(appState: CoreInterfaces.AppState): number {
  let total = 0;

  const monthlyRecurringServicesTotalsRow: CoreInterfaces.RecurringServicesTableRow =
    getRecurringServicePanelTableRows(
      appState,
      Constants.ServiceRecurringType.Monthly
    ).find(
      (row) =>
        row.id === Constants.CustomDataGridColumnId.RecurringServicesTotal
    );
  const quarterlyRecurringServicesTotalsRow: CoreInterfaces.RecurringServicesTableRow =
    getRecurringServicePanelTableRows(
      appState,
      Constants.ServiceRecurringType.Quarterly
    ).find(
      (row) =>
        row.id === Constants.CustomDataGridColumnId.RecurringServicesTotal
    );
  const payrollRecurringServicesTotalsRow: CoreInterfaces.PayrollServicesTableRow =
    getPayrollServicePanelTableRows(appState).find(
      (row) => row.id === Constants.CustomDataGridColumnId.PayrollServicesTotal
    );

  const otherServices = appState.services.filter((eachService) => {
    return (
      [
        Constants.ServiceRecurringType.Yearly,
        Constants.ServiceRecurringType.OneTime,
      ].includes(eachService.data.recurringType) && eachService.state.isSelected
    );
  });

  for (const eachService of otherServices) {
    const serviceStats = getServiceStats(appState, eachService);
    if (
      eachService.data.otherServicesTotalsPanel.isTotalCostOverridenByAdjustment
    ) {
      total += serviceStats.additionalCostsAdjustment;
    } else {
      total += serviceStats.totalPricePerYear;
    }
  }
  if (!!monthlyRecurringServicesTotalsRow) {
    total += monthlyRecurringServicesTotalsRow.totalPricePerYearUnderlineValue;
  }
  if (!!quarterlyRecurringServicesTotalsRow) {
    total +=
      quarterlyRecurringServicesTotalsRow.totalPricePerYearUnderlineValue;
  }
  if (!!payrollRecurringServicesTotalsRow) {
    total += payrollRecurringServicesTotalsRow.totalPricePerYearUnderlineValue;
  }

  return ceil(total);
}

export function getServicesForBusinessUnit(
  servicesConfiguration: CoreInterfaces.ServiceConfiguration[],
  businessUnit: Constants.BusinessUnit
): CoreInterfaces.ServiceConfiguration[] {
  return servicesConfiguration.filter((serviceConfig) =>
    serviceConfig.BusinessUnits.includes(businessUnit)
  );
}
